import type { FirebaseApp } from 'firebase/app';
import { getFunctions, connectFunctionsEmulator, httpsCallable } from 'firebase/functions';
import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  runTransaction,
} from 'firebase/firestore';
import { z } from 'zod';

import {
  sansTypeFondu,
  type AppFieldsFile,
  type AppScreensFile,
} from '@taki/data-model';
import { ficheInstancieeSchema, instanceDocumentSchema, instancePayloadSchema } from './apps-instanciees-schema.js';
export { instancePayloadSchema } from './apps-instanciees-schema.js';

/**
 * LES APPS INSTANCIEES PAR UN CLIENT : `orgs/{orgId}/apps/{appId}`.
 *
 * UNE APP EST UNE DECLARATION INTERPRETEE AU RUNTIME. Le bundle porte, pour
 * chaque app, sa declaration de reference (`taki.fields.json`,
 * `taki.screens.json`, `taki.app.json`) : c'est le MODELE. Quand un client
 * personnalise une app (renomme une porte, ajoute un champ, retire une carte),
 * sa declaration ENTIERE est copiee dans un document, un par app, et c'est ce
 * document que le shell interprete a la place du modele. Pas de document = l'app tourne
 * sur son modele. La regle qui fait foi vit dans `firebase/firestore.rules`,
 * bloc `orgs/{orgId}/apps/{appId}` ; le raisonnement dans
 * `docs/CHANTIERS-SOCLE.md`, chantier `app-declaration-par-org`.
 *
 * CE N'EST PAS UNE FAMILLE, pour les memes raisons que le branding
 * (`branding.ts`, points 1 et 2) : son schema vit ici et non dans
 * `@taki/data-model`, il n'a ni champs standard, ni vues, ni archivage. Un
 * document par app, identifiant FIXE (l'id de l'app), lu par tout membre actif,
 * ecrit via le service serveur par owner ou admin. « Revenir au modele » est une vraie suppression :
 * ce document ne porte aucune donnee metier, seulement la FORME des ecrans, et
 * le modele reste dans le bundle.
 *
 * LE DOCUMENT S'ECRIT EN ENTIER, jamais par patch : une declaration est un
 * tout (un champ retire d'un jeu doit disparaitre des colonnes qui le
 * citent), et c'est la declaration COMPLETE que le Studio client a sous les
 * yeux quand il enregistre. Le service serveur remplace le document entier : le document final est
 * exactement ce qui a ete valide.
 *
 * `modele` EST LE CLICHE DE BASE : la declaration du modele telle qu'elle etait
 * quand le client a copie l'app, avec sa version. Il voyage avec l'instance
 * parce qu'il n'existe nulle part ailleurs (le bundle ne garde que la version
 * courante), et c'est lui qui permet la fusion a trois voies quand le bundle
 * livre un nouveau modele : ce que le client a change se lit en comparant son
 * instance a ce cliche, jamais au modele courant (chantier
 * `app-mise-a-jour-modele`).
 *
 * A LA LECTURE, UN DOCUMENT ILLISIBLE N'EST PAS FATAL : il est rendu avec sa
 * `raison`, et l'hote retombe sur le modele pour cette app-la. Un document
 * pose par une version future du schema, ou abime par l'Admin SDK, ne doit
 * jamais empecher une org entiere d'ouvrir ses apps ; il doit se VOIR dans
 * Parametres, avec sa cause. A L'ECRITURE, rien ne passe : `enregistrer`
 * refuse la declaration avant qu'elle n'atteigne Firestore.
 */

/** La collection des instances d'une org : `orgs/{orgId}/apps`. */
const APPS_COLLECTION = 'apps';

export type FicheInstanciee = z.infer<typeof ficheInstancieeSchema>;

/**
 * UNE DECLARATION COMPLETE : les champs, les ecrans, et ce que la fiche
 * renomme. C'est la forme commune de l'instance et de son modele, et c'est ce
 * que le shell interprete, d'ou qu'elle vienne (bundle ou document).
 */
export interface DeclarationInstanciee {
  champs: AppFieldsFile;
  ecrans: AppScreensFile;
  fiche?: FicheInstanciee;
}

export interface ModeleCopie extends DeclarationInstanciee {
  /** La `version` de `taki.app.json` au moment de la copie. */
  version: string;
}

/**
 * LE DOCUMENT, valide localement puis au serveur : memes clefs fermees.
 * `updatedAt`, `updatedBy` et `revision` sont poses par le service serveur.
 *
 * NON STRICT VOLONTAIREMENT a la racine : a la RELECTURE, une clef inconnue
 * posee par une version future est ignoree plutot que fatale. Les declarations
 * elles-memes restent strictes : un ecran qui porte une clef inconnue est un
 * ecran qu'on ne sait pas interpreter, et le dire vaut mieux que le monter a
 * moitie.
 */
/**
 * CE QU'ON ENREGISTRE : la declaration du client et le cliche du modele qu'elle
 * etend. La forme est celle du document, mais les declarations y sont
 * `object`, pas les types Zod : c'est le meme parti que `createIn` (le type
 * CONCRET n'existe pas chez l'appelant). Le socle tient ses declarations en
 * types STRUCTURELS (`@taki/ui`, qui ne connait aucun schema Zod, et plus
 * larges : un `role` de tri y est une chaine, ici une enum), et c'est de la que
 * viennent les ecritures (l'editeur du client, dans le shell). Les accepter
 * telles quelles et les VALIDER par Zod avant d'ecrire est le contrat honnete ;
 * les typer finement ici obligerait chaque appelant a promettre par un cast ce
 * que `safeParse` verifie deja.
 */
export interface InstanceAEnregistrer {
  readonly champs: object;
  readonly ecrans: object;
  readonly fiche?: object;
  readonly modele: {
    readonly version: string;
    readonly champs: object;
    readonly ecrans: object;
    readonly fiche?: object;
  };
}

/** Une instance lue : la declaration, son modele, et qui l'a touchee en dernier. */
export interface AppInstanciee extends DeclarationInstanciee {
  appId: string;
  modele: ModeleCopie;
  /** `null` tant que le serveur n'a pas resolu l'horodatage (ecriture locale en attente). */
  updatedAt: Date | null;
  updatedBy: string;
}

/**
 * LE RESULTAT D'UNE LECTURE, document par document : soit l'instance, soit la
 * raison pour laquelle elle est inutilisable. `appId` est toujours la, tire du
 * chemin : Parametres peut nommer l'app fautive meme quand le document est
 * vide de sens.
 */
export type LectureInstance =
  | { appId: string; instance: AppInstanciee; raison?: undefined }
  | { appId: string; instance: null; raison: string };

export interface AppsInstancieesRepository {
  optionsChamp(input: object): Promise<Record<string, unknown>>;
  /** Lit l'instance d'une app ; `null` si le document n'existe pas (l'app tourne sur son modele). */
  lire(appId: string): Promise<LectureInstance | null>;
  /** Lit toutes les instances de l'org, une requete de collection. */
  lister(): Promise<LectureInstance[]>;
  /**
   * Suit la collection en direct : rappelle a chaque changement avec la liste
   * COMPLETE des instances. Rend le DESABONNEMENT, a appeler au demontage.
   *
   * Pourquoi un abonnement : un admin qui renomme une porte pendant qu'un
   * collegue travaille dans l'app doit lui changer l'ecran sous les yeux, sans
   * recharger ; et un « retour au modele » doit se voir partout aussitot.
   */
  observer(
    surChangement: (instances: LectureInstance[]) => void,
    surErreur?: (erreur: unknown) => void,
  ): () => void;
  /**
   * Enregistre la declaration ENTIERE d'une app (creation ou remplacement).
   * Valide AVANT d'ecrire, avec les schemas memes du bundle : une declaration
   * refusee leve ici, avec le chemin fautif, au lieu de revenir en
   * `permission-denied` ou, pire, d'etre ecrite puis ignoree a la relecture.
   */
  enregistrer(appId: string, instance: InstanceAEnregistrer): Promise<void>;
  /** Revient au modele : supprime le document, l'app retombe sur la declaration du bundle. */
  reinitialiser(appId: string): Promise<void>;
}

/** Les problemes Zod en une phrase, chemin par chemin. */
function detailsDe(erreur: z.ZodError): string {
  return erreur.issues
    .map((probleme) => `${probleme.path.join('.') || '(racine)'} : ${probleme.message}`)
    .join(' ; ');
}

/**
 * Une declaration est du JSON, et rien d'autre : le detour par `JSON` retire
 * les `undefined` (que Firestore refuse) et garantit qu'aucune valeur non
 * serialisable ne part dans le document.
 */
function enJson<T>(valeur: T): T {
  return JSON.parse(JSON.stringify(valeur)) as T;
}

/** Ce qu'on retient d'un document lu, ou pourquoi on ne le retient pas. */
function lectureDepuis(appId: string, data: DocumentData): LectureInstance {
  /* UN TYPE FONDU le 27/10/2026 (`montant`, `pourcentage`, `duree`, `liens`)
     se relit dans sa forme nouvelle, dans l'instance et dans son cliche du
     modele (`sansTypeFondu`, la relecture de `lireChampsDeclares`) : sans elle,
     le document entier etait refuse et l'app retombait sur son modele. */
  const modele: unknown = data['modele'];
  const verdict = instanceDocumentSchema.safeParse({
    ...data,
    champs: sansTypeFondu(data['champs']),
    ...(modele !== null && typeof modele === 'object' && !Array.isArray(modele)
      ? { modele: { ...modele, champs: sansTypeFondu((modele as Record<string, unknown>)['champs']) } }
      : {}),
  });
  if (!verdict.success) {
    return { appId, instance: null, raison: detailsDe(verdict.error) };
  }
  if (verdict.data.appId !== appId) {
    return {
      appId,
      instance: null,
      raison: `le document dit instancier "${verdict.data.appId}" mais vit sous "${appId}"`,
    };
  }
  const brut = data['updatedAt'] as { toDate?: () => Date } | undefined;
  const updatedAt = brut && typeof brut.toDate === 'function' ? brut.toDate() : null;
  const updatedBy = typeof data['updatedBy'] === 'string' ? data['updatedBy'] : '';
  const instance: AppInstanciee = {
    appId,
    champs: verdict.data.champs,
    ecrans: verdict.data.ecrans,
    modele: verdict.data.modele,
    updatedAt,
    updatedBy,
  };
  if (verdict.data.fiche) instance.fiche = verdict.data.fiche;
  return { appId, instance };
}

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifie courant
 *   (injecte par `createTakiSdk`) ; le serveur utilise l'uid du jeton verifie.
 * @param orgId - l'org de la config chargee au runtime, dans le CHEMIN : une
 *   ecriture ne peut structurellement pas viser une autre org.
 */
export function createAppsInstancieesRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
  app: FirebaseApp = db.app,
  emulator?: { host: string; port: number },
): AppsInstancieesRepository {
  const laCollection = collection(db, 'orgs', orgId, APPS_COLLECTION);
  const reference = (appId: string) => doc(laCollection, appId);

  let optionsCall: ReturnType<typeof httpsCallable<object, Record<string, unknown>>> | undefined;
  let call: ReturnType<typeof httpsCallable<unknown, unknown>> | undefined;
  const write = async (appId: string, instance: InstanceAEnregistrer | null) => {
    const state = await runTransaction(db, async tx => {
      const head = await tx.get(doc(db, 'orgs', orgId, 'appRevisions', appId));
      return { expectedRevision: head.exists() ? head.data()['revision'] : 0 };
    });
    if (!call) {
      const functions = getFunctions(app, 'europe-west1');
      if (emulator) connectFunctionsEmulator(functions, emulator.host, emulator.port);
      call = httpsCallable(functions, 'enregistrerInstance');
    }
    await call({ orgId, appId, ...state, operationId: crypto.randomUUID(), ...(instance ? { instance } : { reset: true }) });
  };
  return {
    async optionsChamp(input: object) {
      if (!optionsCall) {
        const functions = getFunctions(app, 'europe-west1');
        if (emulator) connectFunctionsEmulator(functions, emulator.host, emulator.port);
        optionsCall = httpsCallable(functions, 'optionsStudio');
      }
      return (await optionsCall({ ...input, orgId })).data;
    },
    async lire(appId: string): Promise<LectureInstance | null> {
      const snap = await getDoc(reference(appId));
      if (!snap.exists()) return null;
      return lectureDepuis(appId, snap.data());
    },

    async lister(): Promise<LectureInstance[]> {
      const snap = await getDocs(laCollection);
      return snap.docs.map((d) => lectureDepuis(d.id, d.data()));
    },

    observer(
      surChangement: (instances: LectureInstance[]) => void,
      surErreur?: (erreur: unknown) => void,
    ): () => void {
      return onSnapshot(
        laCollection,
        (snap) => {
          surChangement(snap.docs.map((d) => lectureDepuis(d.id, d.data())));
        },
        (erreur) => {
          // Sans rappel d'erreur, une coupure reseau ou un refus des rules
          // laisserait l'appelant en attente pour toujours : on le lui dit, et
          // c'est a lui de garder le modele a l'ecran.
          surErreur?.(erreur);
        },
      );
    },

    async enregistrer(appId: string, instance: InstanceAEnregistrer): Promise<void> {
      if (!/^[a-z][a-z0-9-]*$/.test(appId)) {
        throw new Error(`apps instanciees : id d'app invalide "${appId}"`);
      }
      const verdict = instancePayloadSchema.safeParse(instance);
      if (!verdict.success) {
        throw new Error(
          `apps instanciees : declaration de "${appId}" refusee avant ecriture (${detailsDe(verdict.error)}). ` +
            'Une declaration qui ne passe pas ses schemas ne serait pas interpretable a la relecture : ' +
            "l'ecrire ne ferait que deplacer l'erreur.",
        );
      }
      await write(appId, enJson(verdict.data));
    },

    async reinitialiser(appId: string): Promise<void> {
      await write(appId, null);
    },
  };
}
